import { useMemo } from 'react';

import type { SuggestionMarkerPair } from '@aline/common';
import { Queries } from '@aline/common';
import { useCounter } from 'react-use';
import type { Editor } from 'slate';
import { useCurrentUserId } from 'src/gql';
import {
  useDocContentChangedCount,
  useMaybeEditor,
  useSuggestionContentChangedCount,
} from 'src/layout-engine';

const EMPTY_SUGGESTION_MARKERS: SuggestionMarkerPair[] = [];

/**
 * Computes the suggestion marker pairs in the current editor. Returns a stable
 * array reference between content ticks so consumers can use it as an effect
 * dependency without churn.
 */
export function useComputeSuggestionMarkers() {
  const editor = useMaybeEditor();
  const currentUserId = useCurrentUserId();
  const { docContentChangedCount } = useDocContentChangedCount();
  const { suggestionContentChangedCount } = useSuggestionContentChangedCount();
  const [suggestionMarkersVersion, { inc: recomputeSuggestions }] = useCounter(0);
  const suggestionMarkers = useMemo(() => {
    return computeSuggestionMarkers(
      editor,
      currentUserId,
      docContentChangedCount,
      suggestionContentChangedCount,
      suggestionMarkersVersion
    );
  }, [
    editor,
    currentUserId,
    docContentChangedCount,
    suggestionContentChangedCount,
    suggestionMarkersVersion,
  ]);

  return { recomputeSuggestions, suggestionMarkers };
}

/**
 * Counter args are read only as memo dependencies; suggestionMarkersVersion is
 * only used to trigger a re-compute from outside callers.
 */
export function computeSuggestionMarkers(
  editor: Editor | null,
  currentUserId: string | null,
  docContentChangedCount: number,
  _suggestionContentChangedCount: number,
  _suggestionMarkersVersion: number
) {
  if (!docContentChangedCount || !editor || !currentUserId) return EMPTY_SUGGESTION_MARKERS;

  return Queries.getAllSuggestionMarkers(editor).filter(marker => {
    const markerAuthorId = marker.start.authorId;
    return typeof markerAuthorId !== 'string' || markerAuthorId === currentUserId;
  });
}
